import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../style/payment_page.css";
import { getSelectedAddress } from "../services/addressService";
import { clearCart, getCartSummary, loadCart } from "../services/cartService";
import { createOrder } from "../services/orderService";

const PAYMENT_METHOD_LABELS = {
  credit_debit: "Credit / Debit Card",
  internet_banking: "Net Banking",
  upi_vpa: "UPI ID",
  gpay: "Google Pay",
  phonepe: "PhonePe",
  qa_coder_wallet: "QA Coder Wallet",
};

const SAVED_CARD_OPTIONS = [
  { id: "saved-icici-3000", label: "ICICI Bank Credit Card ending in 3000", network: "Visa", nickname: "Primary" },
  { id: "saved-hdfc-4643", label: "HDFC Bank Credit Card ending in 4643", network: "RuPay", nickname: "Work" },
  { id: "saved-axis-7237", label: "Axis Bank Credit Card ending in 7237", network: "Mastercard", nickname: "Travel" },
];

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [selectedMethod, setSelectedMethod] = useState("credit_debit");
  const [isPaying, setIsPaying] = useState(false);
  const [paymentData, setPaymentData] = useState({
    cardNumber: "",
    cardExpiry: "",
    cardCvv: "",
    bank: "",
    upiId: "",
  });
  const [orderSummary, setOrderSummary] = useState({
    items: 0,
    subtotal: 0,
    discount: 0,
    delivery: 0,
    totalPayable: 0,
  });
  const [selectedAddress, setSelectedAddress] = useState(null);

  const cartItems = useMemo(() => loadCart(), []);

  useEffect(() => {
    const savedSummary = localStorage.getItem("cartSummary");
    setOrderSummary(savedSummary ? JSON.parse(savedSummary) : getCartSummary(cartItems));
    setSelectedAddress(getSelectedAddress());
  }, [cartItems]);

  const handlePaymentChange = (event) => {
    setSelectedMethod(event.target.value);
  };

  const updatePaymentField = (event) => {
    const { id, value } = event.target;
    setPaymentData((previous) => ({ ...previous, [id]: value }));
  };

  const savedBalanceAmount = Math.min(orderSummary.totalPayable, 1240);

  const isPaymentValid = useMemo(() => {
    if (selectedMethod === "credit_debit") {
      return (
        paymentData.cardNumber.replace(/\s/g, "").length >= 16 &&
        paymentData.cardExpiry.length >= 5 &&
        paymentData.cardCvv.length >= 3
      );
    }

    if (selectedMethod === "internet_banking") {
      return Boolean(paymentData.bank);
    }

    if (selectedMethod === "upi_vpa") {
      return paymentData.upiId.includes("@");
    }

    return true;
  }, [paymentData, selectedMethod]);

  const handlePayment = (event) => {
    event.preventDefault();

    if (!cartItems.length) {
      toast.error("Your cart is empty. Add products before continuing.");
      navigate("/product");
      return;
    }

    if (!selectedAddress) {
      toast.error("Please select a delivery address before payment.");
      navigate("/address");
      return;
    }

    if (!isPaymentValid) {
      toast.error("Please complete the selected payment method details.");
      return;
    }

    setIsPaying(true);

    createOrder({
      items: cartItems,
      summary: orderSummary,
      address: selectedAddress,
      paymentMethod: PAYMENT_METHOD_LABELS[selectedMethod] || selectedMethod,
    });

    window.setTimeout(() => {
      clearCart();
      localStorage.removeItem("cartSummary");
      navigate("/orders-success");
    }, 700);
  };

  return (
    <div className="checkout-wrapper">
      <main className="payment-container">
        <nav className="breadcrumb-nav">
          <NavLink to="/home" className="breadcrumb-item">Home</NavLink>
          <span className="breadcrumb-separator">{">"}</span>
          <NavLink to="/address" className="breadcrumb-item">Address</NavLink>
          <span className="breadcrumb-separator">{">"}</span>
          <span className="breadcrumb-item active-page">Payment</span>
        </nav>

        <section className="payment-hero payment-hero-marketplace">
          <div className="payment-hero-copy">
            <p className="checkout-eyebrow">Checkout Step 3</p>
            <h1>Payment method</h1>
            <p>
              Select a saved balance, card, bank, or UPI option and complete a production-style
              payment experience with invoice and order tracking support.
            </p>
            <div className="payment-progress-strip">
              <span className="progress-step completed">Cart</span>
              <span className="progress-step completed">Address</span>
              <span className="progress-step active">Payment</span>
              <span className="progress-step">Success</span>
            </div>
          </div>
          <div className="payment-hero-side">
            <div className="checkout-step-pill">Secure checkout</div>
            <div className="hero-amount-card">
              <span>Total to pay</span>
              <strong>Rs.{orderSummary.totalPayable.toLocaleString("en-IN")}</strong>
              <small>{orderSummary.items} item(s) protected by secure checkout</small>
            </div>
          </div>
        </section>

        <div className="payment-content-wrapper payment-content-marketplace">
          <div className="payment-options-panel marketplace-panel">
            <div className="payment-info-banner">
              <i className="fas fa-circle-info"></i>
              <span>View all saved payment methods, balance options, and alternate checkout routes from one place.</span>
            </div>

            <section className="payment-section-card">
              <div className="payment-section-head">
                <div>
                  <h3>Your available balance</h3>
                  <p>Apply stored wallet balance first for a fast, low-friction checkout flow.</p>
                </div>
              </div>
              <label className={`payment-option compact ${selectedMethod === "qa_coder_wallet" ? "selected" : ""}`}>
                <input
                  type="radio"
                  name="payment-method"
                  value="qa_coder_wallet"
                  checked={selectedMethod === "qa_coder_wallet"}
                  onChange={handlePaymentChange}
                />
                <i className="fas fa-wallet"></i>
                <div className="payment-option-copy">
                  <strong>Use Rs.{savedBalanceAmount.toLocaleString("en-IN")} of your QA Coder Wallet</strong>
                  <span>Available balance: Rs.6,240.75</span>
                </div>
              </label>
              <div className="offer-code-row">
                <span className="offer-plus">+</span>
                <input type="text" placeholder="Enter Code" />
                <button type="button">Apply</button>
              </div>
            </section>

            <section className="payment-section-card">
              <div className="payment-section-head">
                <div>
                  <h3>Credit & Debit Cards</h3>
                  <p>Choose from saved instruments or pay with a new card.</p>
                </div>
                <span className="section-side-note">Nickname</span>
              </div>

              <div className="saved-card-list">
                {SAVED_CARD_OPTIONS.map((card) => (
                  <label key={card.id} className={`payment-option compact ${selectedMethod === "credit_debit" ? "selected" : ""}`}>
                    <input
                      type="radio"
                      name="payment-method"
                      value="credit_debit"
                      checked={selectedMethod === "credit_debit"}
                      onChange={handlePaymentChange}
                    />
                    <i className="fas fa-credit-card"></i>
                    <div className="payment-option-copy">
                      <strong>{card.label}</strong>
                      <span>{card.network}</span>
                    </div>
                    <span className="payment-option-tag">{card.nickname}</span>
                  </label>
                ))}
              </div>

              <label className={`payment-option ${selectedMethod === "credit_debit" ? "selected" : ""}`}>
                <input
                  type="radio"
                  name="payment-method"
                  value="credit_debit"
                  checked={selectedMethod === "credit_debit"}
                  onChange={handlePaymentChange}
                />
                <i className="fas fa-credit-card"></i>
                <div className="payment-option-copy">
                  <strong>Use a new credit or debit card</strong>
                  <span>Visa, Mastercard, Amex, Diners Club, Maestro, and RuPay supported.</span>
                </div>
              </label>

              {selectedMethod === "credit_debit" && (
                <div id="card-form" className="payment-input-form">
                  <div className="payment-logo-strip">
                    <span>VISA</span>
                    <span>Mastercard</span>
                    <span>Amex</span>
                    <span>Diners</span>
                    <span>RuPay</span>
                  </div>
                  <div className="input-group">
                    <label htmlFor="cardNumber">Card Number</label>
                    <input type="text" id="cardNumber" placeholder="XXXX XXXX XXXX XXXX" maxLength="19" value={paymentData.cardNumber} onChange={updatePaymentField} />
                  </div>
                  <div className="card-details-row">
                    <div className="input-group">
                      <label htmlFor="cardExpiry">Expiry (MM/YY)</label>
                      <input type="text" id="cardExpiry" placeholder="MM/YY" maxLength="5" value={paymentData.cardExpiry} onChange={updatePaymentField} />
                    </div>
                    <div className="input-group">
                      <label htmlFor="cardCvv">CVV</label>
                      <input type="password" id="cardCvv" placeholder="XXX" maxLength="3" value={paymentData.cardCvv} onChange={updatePaymentField} />
                    </div>
                  </div>
                </div>
              )}
            </section>

            <section className="payment-section-card">
              <div className="payment-section-head">
                <div>
                  <h3>Buy now, Pay later</h3>
                  <p>Deferred payment flow for coverage of alternative checkout experiences.</p>
                </div>
              </div>
              <label className="payment-option compact muted-option">
                <input type="radio" name="payment-method-alt" disabled />
                <i className="fas fa-hourglass-half"></i>
                <div className="payment-option-copy">
                  <strong>Pay Later</strong>
                  <span>Available credit: Rs.10,000</span>
                </div>
              </label>
            </section>

            <section className="payment-section-card">
              <div className="payment-section-head">
                <div>
                  <h3>Another payment method</h3>
                  <p>Net banking, UPI, app-based payments, and alternate checkout rails.</p>
                </div>
              </div>

              <label className={`payment-option ${selectedMethod === "internet_banking" ? "selected" : ""}`}>
                <input
                  type="radio"
                  name="payment-method"
                  value="internet_banking"
                  checked={selectedMethod === "internet_banking"}
                  onChange={handlePaymentChange}
                />
                <i className="fas fa-university"></i>
                <div className="payment-option-copy">
                  <strong>Net Banking</strong>
                  <span>Select your bank and continue through the secure banking gateway.</span>
                </div>
              </label>

              {selectedMethod === "internet_banking" && (
                <div id="net-banking-form" className="payment-input-form">
                  <div className="input-group">
                    <label htmlFor="bank">Choose an option</label>
                    <select id="bank" value={paymentData.bank} onChange={updatePaymentField}>
                      <option value="">Select Bank</option>
                      <option value="hdfc">HDFC Bank</option>
                      <option value="icici">ICICI Bank</option>
                      <option value="sbi">State Bank of India</option>
                      <option value="axis">Axis Bank</option>
                    </select>
                  </div>
                </div>
              )}

              <label className={`payment-option ${selectedMethod === "upi_vpa" ? "selected" : ""}`}>
                <input
                  type="radio"
                  name="payment-method"
                  value="upi_vpa"
                  checked={selectedMethod === "upi_vpa"}
                  onChange={handlePaymentChange}
                />
                <i className="fas fa-qrcode"></i>
                <div className="payment-option-copy">
                  <strong>Scan and Pay with UPI / Enter VPA</strong>
                  <span>You can scan QR on the confirmation page or pay using a verified UPI ID.</span>
                </div>
              </label>

              {selectedMethod === "upi_vpa" && (
                <div id="upi-form" className="payment-input-form">
                  <div className="input-group">
                    <label htmlFor="upiId">Enter UPI ID (VPA)</label>
                    <div className="upi-input-wrapper">
                      <input type="text" id="upiId" placeholder="username@bankupi" value={paymentData.upiId} onChange={updatePaymentField} />
                      <button id="upi-verify-btn" type="button">
                        Verify
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <label className={`payment-option ${selectedMethod === "gpay" ? "selected" : ""}`}>
                <input type="radio" name="payment-method" value="gpay" checked={selectedMethod === "gpay"} onChange={handlePaymentChange} />
                <i className="fab fa-google-pay"></i>
                <div className="payment-option-copy">
                  <strong>GPay (Google Pay)</strong>
                  <span>One-tap app based payment journey for mobile-first confirmation flow.</span>
                </div>
              </label>

              <label className={`payment-option ${selectedMethod === "phonepe" ? "selected" : ""}`}>
                <input type="radio" name="payment-method" value="phonepe" checked={selectedMethod === "phonepe"} onChange={handlePaymentChange} />
                <i className="fas fa-mobile-screen-button"></i>
                <div className="payment-option-copy">
                  <strong>PhonePe</strong>
                  <span>Alternate wallet and UPI experience for real-world regression coverage.</span>
                </div>
              </label>
            </section>

            <div className="payment-trust-band">
              <span><i className="fas fa-lock"></i> PCI-style secure form</span>
              <span><i className="fas fa-bolt"></i> Fast confirmation</span>
              <span><i className="fas fa-headset"></i> Support-ready payment flow</span>
            </div>

            <button id="pay-now-btn" className="pay-now-btn" onClick={handlePayment} disabled={!isPaymentValid || isPaying}>
              {isPaying ? "Processing Order..." : "Use this payment method"}
            </button>
          </div>

          <aside className="order-summary-panel summary-marketplace-panel">
            <button className="summary-top-cta" type="button" onClick={handlePayment} disabled={!isPaymentValid || isPaying}>
              {isPaying ? "Processing..." : "Use this payment method"}
            </button>

            <div className="summary-details">
              <div className="summary-line">
                <span>Items:</span>
                <span>Rs.{orderSummary.subtotal.toLocaleString("en-IN")}</span>
              </div>
              <div className="summary-line">
                <span>Delivery:</span>
                <span>{orderSummary.delivery === 0 ? "FREE" : `Rs.${orderSummary.delivery.toLocaleString("en-IN")}`}</span>
              </div>
              <div className="summary-line fee-line">
                <span>Marketplace Fee</span>
                <span>Rs.0</span>
              </div>
              <div className="summary-line">
                <span>Total:</span>
                <span>Rs.{orderSummary.subtotal.toLocaleString("en-IN")}</span>
              </div>
              <div className="summary-line promo-line">
                <span>Promotion Applied:</span>
                <span>- Rs.{orderSummary.discount.toLocaleString("en-IN")}</span>
              </div>

              <div className="summary-line total-payable order-total-marketplace">
                <span>Order Total:</span>
                <span>Rs.{orderSummary.totalPayable.toLocaleString("en-IN")}</span>
              </div>

              {selectedAddress && (
                <div className="summary-line address-summary-block" style={{ display: "block", marginTop: "20px" }}>
                  <strong>Delivering to</strong>
                  <p style={{ marginTop: "8px" }}>
                    {selectedAddress.name}, {selectedAddress.phone}
                    <br />
                    {selectedAddress.address1}
                    {selectedAddress.address2 ? `, ${selectedAddress.address2}` : ""}
                    <br />
                    {selectedAddress.city}, {selectedAddress.state} - {selectedAddress.pincode}
                  </p>
                </div>
              )}

              <div className="summary-items-list">
                {cartItems.map((item) => (
                  <div key={item.id} className="summary-item-row">
                    <div className="summary-item-copy">
                      <span>{item.name}</span>
                      <small>{item.category} · Qty {item.quantity}</small>
                    </div>
                    <strong>Rs.{(Number(item.price) * Number(item.quantity)).toLocaleString("en-IN")}</strong>
                  </div>
                ))}
              </div>

              <p className="security-note">
                <i className="fas fa-lock"></i> All transactions are secure and encrypted.
              </p>
            </div>

            <div className="summary-footer-links">
              <NavLink to="/address">Edit Address</NavLink>
              <NavLink to="/add-to-cart">Back to Cart</NavLink>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
